import apiAuthClient from '@/services/apiAuthClient';
import apiClient from '@/services/apiClient';

import errorHandler from '@/utilities/serverErrorHandler';

const endPoints = {
  directors: {
    add: 'addicdirectors',
    edit: 'updateicdirectors',
    get: 'geticdirectors',
    fieldId: 'ua_icapplicationdirectorsid'
  },
  activities: {
    add: 'addicprojectactivities',
    edit: 'updateicprojectactivities',
    get: 'geticprojectactivities',
    fieldId: 'ua_icactivitylineid'
  },
  expertise: '',
  extraFunds: '',
  competitors: {
    add: 'addicapplicationmaincompetitors',
    edit: 'updateicapplicationmaincompetitors',
    get: 'geticapplicationmaincompetitors',
    fieldId: 'ua_icapplicationmaincompetitorid'
  },
  jobs: {
    add: 'addicprojectforecastjobs',
    edit: 'updateicprojectforecastjobs',
    get: 'geticprojectforecastjobs',
    fieldId: 'ua_icprojectforecastjobsid'
  },
  assets: {
    add: 'AddICAssets',
    edit: 'UpdateICAssets',
    get: 'GetICAssetsByClaimId',
    fieldId: 'ua_icassetid'
  },
  externalFunding: {
    add: 'AddICApplicationExternalFundings',
    edit: 'UpdateICApplicationExternalFundings',
    get: 'GetICApplicationExternalFundings',
    fieldId: 'ua_icapplicationexternalfundingid'
  },
  shareholders: {
    add: 'AddICApplicationShareholders',
    edit: 'UpdateICApplicationShareholders',
    get: 'GetICAppShareholdersByAppID',
    fieldId: 'ua_icapplicationshareholderid'
  }
};

export const childEntities = {
  namespaced: true,
  state: {
    directors: [],
    activities: [],
    expertise: [],
    externalFunding: [],
    extraFunds: [],
    competitors: [],
    jobs: [],
    assets: [],
    activityTypes: [],
    verifiedSuppliers: [],
    shareholders: [],
    currentItemId: ''
  },
  mutations: {
    ADD (state, payload) {
      const currentState = state[payload.itemType];
      currentState.push(payload.data);
      state[payload.itemType] = currentState;
    },
    UPDATE (state, payload) {
      state[payload.itemType] = payload.data;
    },
    SET_CURRENT_ID (state, editingId) {
      state.currentItemId = editingId;
    },
    UPDATE_ACTIVITY_TYPES (state, payload) {
      state.activityTypes = payload;
    },
    ADD_VERIFIED_SUPPLIER (state, payload) {
      state.verifiedSuppliers = payload;
    }
  },
  actions: {
    fetchItems ({ commit, dispatch, rootGetters }, payload) {
      dispatch('ui/updateInterstitial', { isLoading: true, message: 'Fetching your items...' }, { root: true });
      const activeApplication = rootGetters['application/getActiveApplication'];
      const contractId = activeApplication === 'small' ? process.env.VUE_APP_CONTRACT_ID_SMALL : process.env.VUE_APP_CONTRACT_ID_LARGE;
      let method = 'post';

      if (payload === 'externalFunding' || payload === 'shareholders') {
        method = 'get';
      }

      return apiAuthClient[method](`${endPoints[payload].get}/${contractId}`, rootGetters['auth/getAccessToken']).then(response => {
          if (response.status === 200) {
            commit('UPDATE', {
              itemType: payload,
              data: response.data
            });
            return response;
          }
      }).catch((err) => {
        if (err.response.status === 401) {
          dispatch('auth/handleUnauthorised', { retryFunction: 'childEntities/fetchItems', retryPayload: payload }, { root: true });
        } else {
          return errorHandler('childEntityErrors', err, 'get');
        }
      }).finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false }, { root: true });
      });
    },
    fetchAssetItems ({ commit, dispatch, rootGetters }, payload) {
      dispatch('ui/updateInterstitial', { isLoading: true, message: 'Fetching your items...' }, { root: true });

      return apiAuthClient.post(`${endPoints.assets.get}`, payload, rootGetters['auth/getAccessToken']).then(response => {
        if (response.status === 200) {
          commit('UPDATE', {
            itemType: 'assets',
            data: response.data
          });
          return response;
        }
    }).catch((err) => {
      if (err.response.status === 401) {
        dispatch('auth/handleUnauthorised', { retryFunction: 'childEntities/fetchAssetItems', retryPayload: payload }, { root: true });
      } else {
        return errorHandler('childEntityErrors', err, 'get');
      }
    }).finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false }, { root: true });
      });
    },
    addItem ({ commit, dispatch, rootGetters }, payload) {
      dispatch('ui/updateInterstitial', { isLoading: true, message: 'Adding your item...' }, { root: true });
      return apiAuthClient.post(`${endPoints[payload.itemType].add}`, [payload.data], rootGetters['auth/getAccessToken']).then(response => {
        if (response.status === 200) {
          commit('ADD', {
            itemType: payload.itemType,
            data: response.data[0]
          });
          return true;
        }
      }).catch((err) => {
        if (err.response.status === 401) {
          dispatch('auth/handleUnauthorised', { retryFunction: 'childEntities/addItem', retryPayload: payload }, { root: true });
        } else {
          throw errorHandler('childEntityErrors', err, 'post');
        }
      }).finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
      });
    },
    editById ({ commit, dispatch, state, rootGetters }, payload) {
      const itemList = state[payload.itemType];
      const fieldId = endPoints[payload.itemType].fieldId;
      const itemToUpdate = itemList.findIndex(item => item[fieldId] === payload.data[fieldId]);

      itemList.splice(itemToUpdate, 1, payload.data);

      dispatch('ui/updateInterstitial', { isLoading: true, message: 'editing your item...' }, { root: true });
      return apiAuthClient.put(`${endPoints[payload.itemType].edit}`, itemList, rootGetters['auth/getAccessToken']).then(response => {
        if (response.status === 200) {
          commit('UPDATE', {
            itemType: payload.itemType,
            data: itemList
          });
          return true;
        }
      }).catch((err) => {
        if (err.response.status === 401) {
          dispatch('auth/handleUnauthorised', { retryFunction: 'childEntities/editById', retryPayload: payload }, { root: true });
        } else {
          throw errorHandler('childEntityErrors', err, 'put');
        }
      }).finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
      });
    },
    deleteById ({ commit, state, dispatch, rootGetters }, payload) {
      const itemList = state[payload.itemType];
      const fieldId = endPoints[payload.itemType].fieldId;
      const itemToUpdate = itemList.findIndex(item => item[fieldId] === payload.id);
      const item = { ...itemList[itemToUpdate], statecode: 1 };

      itemList.splice(itemToUpdate, 1, item);

      dispatch('ui/updateInterstitial', { isLoading: true, message: 'deleting your item...' }, { root: true });
      return apiAuthClient.put(`${endPoints[payload.itemType].edit}`, [item], rootGetters['auth/getAccessToken']).then(response => {
        if (response.status === 200) {
          commit('UPDATE', {
            itemType: payload.itemType,
            data: itemList
          });
          return true;
        }
      }).catch((err) => {
        if (err.response.status === 401) {
          dispatch('auth/handleUnauthorised', { retryFunction: 'childEntities/deleteById', retryPayload: payload }, { root: true });
        } else {
          throw errorHandler('childEntityErrors', err, 'delete');
        }
      }).finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
      });
    },
    setCurrentId ({ commit }, payload) {
      commit('SET_CURRENT_ID', payload);
    },
    getActivityTypeList ({ commit, rootGetters }) {
      const activeApplication = rootGetters['application/getActiveApplication'];
      const contractId = activeApplication === 'small' ? process.env.VUE_APP_CONTRACT_ID_SMALL : process.env.VUE_APP_CONTRACT_ID_LARGE;

      return apiClient.get(`/INVESTMENTCENTRE/GetICFundActivityTypes/${contractId}`).then((res) => {
        const activityOptions = [];
        res.data.forEach((item) => {
            activityOptions.push({
              value: item.ua_icfundactivitytypeid,
              label: item.ua_activitytype
            });
        });

        commit('UPDATE_ACTIVITY_TYPES', activityOptions);
      });
    },
    getVerifiedSupplierList ({ commit, rootGetters }) {
      const activeApplication = rootGetters['application/getActiveApplication'];
      const contractId = activeApplication === 'small' ? process.env.VUE_APP_CONTRACT_ID_SMALL : process.env.VUE_APP_CONTRACT_ID_LARGE;

      return apiClient.get(`/INVESTMENTCENTRE/GetICProjectSuppliers/${contractId}`).then((res) => {
        const verifiedSuppliers = [];

      res.data.forEach(item => {
          // loop through suppliers and check if they have expertise associated to them
          if (item.ua_icsupplierexpertise) {
            const expertise = [];

            item.ua_icsupplierexpertise.forEach(expItem => {
              expertise.push(
                expItem.ua_name ? expItem.ua_name : 'Other'
              );
            });
            verifiedSuppliers.push({
              label: item.ua_suppliername,
              value: item.ua_suppliername,
              expertise: expertise
            });
            // If so loop through the expertise and add an item to the supplierExpertise array
          }
        });

        // Send the supplier expertise data to mutation
        commit('ADD_VERIFIED_SUPPLIER', verifiedSuppliers);
      });
    }
  },
  getters: {
    getItems: state => (itemType) => {
      const items = state[itemType];
      if (!items.length) {
        return items;
      }
      return items.filter(item => item.statecode !== 1);
    },
    getEligibleActivities: state => (claimId) => {
      const items = state.activities;
      const eligible = items.filter(item => item.ua_outcome === 953880000 && (item.ua_claim === null || item.ua_claim === claimId));

      const eligibleArr = [];

      eligible.forEach((activity) => {
        eligibleArr.push(
          {
            label: `${activity.ua_activity} - ${activity.ua_activitydescription} - Supplied by: ${activity.ua_suppliernamenotinlist}`,
            value: activity.ua_icactivitylineid,
            selected: activity.ua_claim === claimId
          }
        );
      });
      return eligibleArr;
    },
    getCurrentItemId (state) {
      return state.currentItemId;
    },
    getCurrentItem: state => (itemType) => {
      const fieldId = endPoints[itemType].fieldId;
      const result = state[itemType].find(item => item[fieldId] === state.currentItemId);
      return result;
    },
    getActivityTypes: (state) => {
      return state.activityTypes;
    },
    getActivityTypeName: state => (id) => {
      const type = state.activityTypes.find(item => item.value === id);
      return type ? type.label : '';
    },
    getSupplierName: state => (id) => {
      const supplier = state.verifiedSuppliers.find(item => item.value === id);
      return supplier ? supplier.label : '';
    },
    getActivityTotalCost: (state) => {
      const activities = state.activities;
      let totalCost = 0;
      if (activities.length) {
        activities.forEach((activity) => {
          if (activity.statecode !== 1) {
            const costToAdd = parseFloat(activity.ua_activitytotalcost).toFixed(2);
            totalCost += parseFloat(costToAdd);
          }
        });
      }
      return parseFloat(totalCost);
    },
    getTotalExtraFunds: (state) => {
      const extraFunds = state.extraFunds;
      let totalCost = 0;
      if (extraFunds.length) {
        extraFunds.forEach((extraFund) => {
          totalCost += parseInt(extraFund.ua_facilitylimit, 10);
        });
      }

      return totalCost;
    },
    getTotalJobs: (state) => {
      let total = 0;
      const sumFunction = (a, b) => a + b;

      state.jobs.forEach(job => {
        total = sumFunction(total, parseInt(job.ua_quantity));
      });
      return total;
    },
    getVerifiedSuppliers: (state) => {
      return state.verifiedSuppliers;
    }
  }
};
